import React, { FormEvent, useEffect, useState } from 'react';
import { MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAccountSpecificFeature } from '../../acl/features';
import { State } from '../../reducers';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { loadTicketCustomFields } from '../actions';
import ActionBar from '../../../mui-custom/ActionBar/ActionBar';
import { CustomField } from '../../../components/CustomField';
import { api } from '../../../lib/api';
import { CasaviCustomField, CasaviFieldType, AccountSpecificFeature } from '@api/v1';
import { hideNotification, showNotification } from '../../actions';
import { DropzoneFile } from '../../../components/Dropzone/types';
import { containsFailedFiles, isReadyToUpload } from '../../../components/Dropzone/validation';
import { MaterialDropzone } from '../../../components/Dropzone';
import Circle from '@mui/icons-material/Circle';

const CASAVI_MAX_FILE_SIZE = 50 * 1024 * 1024;

const titleFieldNames = ['Anliegen', 'Concern', 'Issue', 'Title', 'Titel', 'Problema'];
const descriptionFieldNames = [
	'Beschreibung',
	'Description',
	'Descrizione',
	'Descripción',
	'Kuvaus'
];

export const PRIORITIES = [
	{ value: 3, color: 'error', label: 'tickets:priority_high' },
	{ value: 2, color: 'warning', label: 'tickets:priority_medium' },
	{ value: 1, color: 'success', label: 'tickets:priority_low' }
];

const populateFields = (
	customFields: CasaviCustomField[] = [],
	values: { [x: number]: unknown } = {},
	files: { [x: number]: DropzoneFile[] } = {}
) => {
	const getFieldByNameOrType = (fieldNames: string[], fieldType: CasaviFieldType) =>
		customFields.find((f) => fieldNames.includes(f.name)) ??
		customFields.find((f) => !fieldNames.includes(f.name) && f.fieldType === fieldType);

	const fieldTitle = getFieldByNameOrType(titleFieldNames, CasaviFieldType.TEXTFIELD);
	const title = values[fieldTitle?.id];

	const fields: Record<number, unknown> = {};
	customFields.forEach((customField) => {
		const fieldValue = values[customField.id];
		if (fieldValue) {
			fields[customField.id] = fieldValue;
			return;
		}
		const customFieldFiles = files[customField.id];
		if (customFieldFiles) {
			fields[customField.id] = customFieldFiles;
		}
	});

	return { fields, title };
};

export const isTitleField = (customField: CasaviCustomField): boolean => {
	return titleFieldNames.includes(customField.name);
};

export const isDescriptionField = (customField: CasaviCustomField): boolean => {
	return descriptionFieldNames.includes(customField.name);
};

interface AddCustomFieldsPageProps {
	propertyManagerId: number;
	propertyExternalId: number | string;
	ticketTypeId: number;
	casaviTenantId: number;
	casaviContactId: number;
}

export const AddCustomFieldsPage = (props: AddCustomFieldsPageProps) => {
	const hasTicketsCreation = useAccountSpecificFeature(AccountSpecificFeature.TicketCreation);
	const { propertyManagerId, propertyExternalId, ticketTypeId, casaviTenantId, casaviContactId } =
		props;

	const { t } = useTranslation();
	const dispatch = useDispatch();
	const history = useHistory();
	const { spacing } = useTheme();

	const { ticketCustomFields } = useSelector((state: State) => state.tickets);

	const { search } = useLocation();
	const [priority, setPriority] = useState(2);
	const [values, setValues] = useState<{ [x: number]: unknown }>({});
	const [files, setFiles] = useState<{ [x: number]: DropzoneFile[] }>({});

	useEffect(() => {
		dispatch(loadTicketCustomFields({ casaviTenantId, ticketTypeId }));
	}, [search, casaviTenantId, ticketTypeId]);

	// set initial values for custom fields
	const setInitialValues = () => {
		const initialValues: { [x: number]: unknown } = {};
		ticketCustomFields.forEach((customField) => {
			if (isTitleField(customField)) {
				initialValues[customField.id] = '';
			} else if (isDescriptionField(customField)) {
				initialValues[customField.id] = '';
			} else if (customField.fieldType === CasaviFieldType.CHECKBOX) {
				initialValues[customField.id] = false;
			} else {
				initialValues[customField.id] = null;
			}
		});
		setValues(initialValues);
	};
	// check if all required fields are filled
	const requiredFieldsFilled = ticketCustomFields.every(
		(customField) =>
			!customField.required ||
			(typeof values[customField.id] === 'string' && /\S/.test(values[customField.id] as string)) ||
			files[customField.id]?.length ||
			(typeof values[customField.id] !== 'string' &&
				values[customField.id] !== undefined &&
				values[customField.id] !== null)
	);

	useEffect(() => {
		setInitialValues();
	}, [ticketCustomFields]);

	const allFiles = Object.values(files).flat();
	const isUploaded = isReadyToUpload(allFiles);
	const uploadFailed = containsFailedFiles(allFiles);

	const setField = (customFieldId: number) => (option: any) => {
		setValues((prev) => {
			if (option instanceof Event || option?.target) {
				return {
					...prev,
					[customFieldId]:
						option.target.type === 'checkbox' ? option.target.checked : option.target.value ?? null
				};
			}
			return {
				...prev,
				[customFieldId]: option ?? null
			};
		});
	};

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		try {
			const { title, fields } = populateFields(ticketCustomFields, values, files);
			const { id } = await api.post('/api/v1/tickets/create-casavi-ticket', {
				title: title ?? '', // undefined title, description are not allowed, but blank string is accepted for auto-generated title from ticket type name
				description: '',
				TicketTypeId: ticketTypeId,
				CommunityId: Number(propertyExternalId),
				TenantId: Number(casaviTenantId),
				ContactId: Number(casaviContactId),
				priority,
				fields
			});
			if (!id) throw new Error('Failed to submit form');

			dispatch(showNotification(t('tickets:ticket_created_message'), 'success', true));
			history.push(`/app/tickets/detail/${id}`);
		} catch (error) {
			dispatch(showNotification(t('tickets:ticket_submit_error'), 'error', true));
		} finally {
			setTimeout(() => dispatch(hideNotification()), 5000);
		}
	};

	if (!hasTicketsCreation) return null;
	if (!propertyManagerId || !propertyExternalId || !ticketTypeId || !casaviTenantId) return null;
	return (
		<Stack sx={{ m: 2, py: 1 }}>
			<br />
			<form onSubmit={handleSubmit}>
				<Stack>
					<Typography variant="h6" sx={{ my: 2 }}>
						{t('tickets:priority')}
					</Typography>
					<Select value={priority} onChange={({ target }) => setPriority(Number(target.value))}>
						{PRIORITIES.map(({ value, color, label }) => (
							<MenuItem key={value} value={value}>
								<Stack flexDirection="row">
									<Circle color={color} sx={{ fontSize: spacing(2), color, mt: 0.5 }} />
									&nbsp;&nbsp;{t(label)}
								</Stack>
							</MenuItem>
						))}
					</Select>
				</Stack>
				{(ticketCustomFields || []).map((customField) =>
					[CasaviFieldType.DOCUMENT, CasaviFieldType.SERVICE_PROVIDER_CONTRACT].includes(
						customField.fieldType
					) ? (
						<Stack key={customField.id}>
							{!!customField.name && (
								<Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
									{customField.name} {customField.required && '*'}
								</Typography>
							)}
							<MaterialDropzone
								casavi // casavi specific added prop to passthrough uploading temp files to casavi
								casaviUploadFileType={'tickets'}
								text={t('orderReply:attachment_placeholder')}
								files={files[customField.id]}
								onDrop={(f: DropzoneFile) => {
									setFiles((files) => ({
										...files,
										[customField.id]: [...(files?.[customField.id] || []), f]
									}));
								}}
								onRemove={(tempId: string) => {
									setFiles((files) => ({
										...files,
										[customField.id]: (files?.[customField.id] || []).filter(
											(f: DropzoneFile) => tempId !== f.tempId
										)
									}));
								}}
								onChangeFile={(tempId, data) => {
									setFiles((files) => ({
										...files,
										[customField.id]: (files?.[customField.id] || []).map((f: DropzoneFile) =>
											f.tempId === tempId ? { tempId, ...data } : f
										)
									}));
								}}
								maxSize={CASAVI_MAX_FILE_SIZE}
								accept={
									CasaviFieldType.SERVICE_PROVIDER_CONTRACT === customField.fieldType
										? 'application/pdf'
										: undefined
								}
							/>
						</Stack>
					) : (
						<CustomField
							key={customField.id}
							value={values[customField.id]}
							{...customField}
							onChange={setField(customField.id)}
						/>
					)
				)}
				<br />
				<ActionBar
					primary={{
						children: t('tickets:submit_ticket'),
						type: 'submit',
						disabled: !isUploaded || uploadFailed || !requiredFieldsFilled
					}}
				/>
			</form>
		</Stack>
	);
};
